import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { Grid, Typography, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { endOfDay, startOfDay } from 'date-fns'

import { IData, IDataList } from '../FinanceTableLists'
import { TableFixed } from '../../../../components/TableFixed'
import { useRest } from '../../../../components/Context'
import { DataPicker } from '../../../../components/DatePicker'
import { generateParams } from '../../../../utils'

const useStyles = makeStyles((theme: Theme) => ({
  formWrap: {
    paddingTop: theme.spacing(4),
    width: '100%',
    height: '100%',
    minWidth: 400,
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    maxWidth: '100%',
    maxHeight: 40,
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
  section: {
    width: '100%',
    height: '100%',
    padding: '0 32px',
    background: 'rgba(216, 216, 216, 0.05)',
    flexDirection: 'column',
  },
  datePickerWrap: {
    justifyContent: 'space-between',
    paddingTop: theme.spacing(3),
  },
}))

const GridRow: FC = ({ children }) => {
  const classes = useStyles()

  return (
    <Grid container spacing={4} className={classes.datePickerWrap}>
      {children}
    </Grid>
  )
}

interface FinanceSectionProps {
  title: string
  data: IData
}

export const FinanceSection: FC<FinanceSectionProps> = ({ title, data }) => {
  const classes = useStyles()

  const formMethods = useForm({
    mode: 'onBlur',
  })
  const { watch, getValues } = formMethods

  const { rxios, rxiosGeneral } = useRest()

  const xios = title === 'Revenue by Asset' ? rxios : rxiosGeneral

  const [loading, setLoading] = useState<boolean>(false)
  const [values, setValues] = useState<IDataList>()
  // eslint-disable-next-line
  const [startDateSelected, setStartDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [endDateSelected, setEndDateSelected] = useState<any>('')

  const loadData = useCallback(
    async (startDate = '', endDate = '') => {
      let cleanupFunction = false
      setLoading(true)

      // Don't push to history when loading data - causes redirect issues
      // history.push(`finance`)

      xios.get(`${data.request}?${generateParams({ startDate, endDate })}`).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          setValues(next)
          if (!cleanupFunction) setLoading(false)
        },
        (error: Error) => {
          toast.error(error?.message)
          setLoading(false)
        },
        () => {
          setLoading(false)
        },
      )
      cleanupFunction = true
    },
    [data.request, xios],
  )

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'startDate') setStartDateSelected(value?.startDate?.toISOString())
      if (name === 'endDate') setEndDateSelected(value?.endDate?.toISOString())
    })
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    ;(async () => {
      if (data.request) {
        setEndDateSelected(endDateSelected === '1970-01-01' ? '' : endDateSelected)
        await loadData(startDateSelected || '', endDateSelected || '')
      }
    })()
    // eslint-disable-next-line
  }, [startDateSelected, endDateSelected])

  const firstSum = useMemo(() => {
    return Number(values?.total || values?.sub_total || '0')
  }, [values])

  const secondSum = useMemo(() => {
    return Number(values?.revenue)
  }, [values])

  return (
    <>
      <Grid item xs={12} className={classes.formWrap}>
        <FormProvider {...formMethods}>
          <Grid item xs={12} className={classes.container}>
            <Typography variant={'h6'} className={classes.title}>
              {title}
            </Typography>
          </Grid>

          <Grid item xs={12} className={classes.section}>
            <FormProvider {...formMethods}>
              <GridRow>
                <Grid item xs={6}>
                  <DataPicker
                    placeholder={'Start Date'}
                    margin={0}
                    dataPickerName={'startDate'}
                    required={false}
                    width={'100%'}
                    maxDate={getValues().endDate}
                    formatValue={startOfDay}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DataPicker
                    placeholder={'End Date'}
                    margin={0}
                    dataPickerName={'endDate'}
                    required={false}
                    width={'100%'}
                    minDate={getValues().startDate}
                    formatValue={endOfDay}
                  />
                </Grid>
              </GridRow>
            </FormProvider>

            <TableFixed
              columns={data.columns}
              data={values?.pairs || values?.assets}
              firstSum={firstSum}
              secondSum={secondSum}
              loading={loading}
            />
          </Grid>
        </FormProvider>
      </Grid>
    </>
  )
}
